// client/src/App.js - Main React Application
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import BookingCalendar from './components/BookingCalendar.tsx';
import AdminPanel from './components/AdminPanel.tsx';
import Login from './components/Login.jsx';
import './App.css';

function App() {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check for existing admin token on app load
  useEffect(() => {
    const checkAdminAuth = () => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        // In a real app, you would verify the token with the backend
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const currentTime = Date.now() / 1000;
          
          if (payload.exp > currentTime) {
            setIsAdminAuthenticated(true);
          } else {
            localStorage.removeItem('admin_token');
          }
        } catch (error) {
          localStorage.removeItem('admin_token');
        }
      }
      setLoading(false);
    };

    checkAdminAuth();
  }, []);

  // Protected route component for admin
  const ProtectedAdminRoute = ({ children }) => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        </div>
      );
    }
    
    return isAdminAuthenticated ? children : <Navigate to="/admin/login" replace />;
  };

  // Admin login component wrapper
  const AdminLoginWrapper = () => {
    if (isAdminAuthenticated) {
      return <Navigate to="/admin" replace />;
    }
    
    return (
      <Login 
        onLoginSuccess={() => setIsAdminAuthenticated(true)}
      />
    );
  };

  // Admin panel wrapper
  const AdminPanelWrapper = () => (
    <AdminPanel 
      onLogout={() => {
        setIsAdminAuthenticated(false);
        localStorage.removeItem('admin_token');
      }}
    />
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App min-h-screen bg-gray-50">
        <Routes>
          {/* Public booking calendar */}
          <Route path="/" element={<BookingCalendar />} />
          
          {/* Admin login */}
          <Route path="/admin/login" element={<AdminLoginWrapper />} />
          
          {/* Protected admin panel */}
          <Route 
            path="/admin" 
            element={
              <ProtectedAdminRoute>
                <AdminPanelWrapper />
              </ProtectedAdminRoute>
            } 
          />
          
          {/* Redirect any other admin routes to main admin */}
          <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
          
          {/* 404 fallback */}
          <Route path="*" element={
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
                <p className="text-gray-600 mb-4">Page not found</p>
                <a href="/" className="text-blue-500 hover:text-blue-700 underline">
                  Go back to booking calendar
                </a>
              </div>
            </div>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
